import React, { useMemo } from 'react'
import { StyleSheet, View, Text, ScrollView } from 'react-native'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
import { Entypo } from '@expo/vector-icons'
import moment from 'moment'

// stores
import { useLabourStore } from '../../../stores/labour'

// components
import HomeDetailsHeader from '../../../components/home/HomeDetailsHeader'

// styles
import { colorPalette, fontPalette, globalStyles } from '../../../static/styles/global'

const AnnouncementsScreen = ({ navigation }: any): JSX.Element => {
  const { t } = useTranslation()
  const labourStore = useLabourStore()

  const announcementsList = useMemo(() => {
    const filtered: Array<{ description: string; level: string; language: string; postDate: string }> = []

    if (labourStore?.AnnouncmentsData) {
      labourStore.AnnouncmentsData.forEach((data) => {
        const postDate = new Date(data?.postDate + 'Z')
        const expDate = new Date(data?.expDate + 'Z')
        const now = new Date()

        if (data.isActive && expDate > now && postDate <= now) {
          let selectedDetail = data.announcementDetails?.find(
            (de) => de.language === i18next.language && de.description != ''
          )
          if (!selectedDetail) {
            selectedDetail = data.announcementDetails?.find(
              (de) => de.language === 'ENG'
            )
          }
          if (selectedDetail) {
            filtered.push({
              description: selectedDetail.description,
              level: data.level,
              language: selectedDetail.language,
              postDate: data.postDate
            })
          }
        }
      })
    }
    return filtered
  }, [labourStore?.AnnouncmentsData])

  return (
    <ScrollView style={styles.mainContainer}>
      <View style={[globalStyles.primaryBgColor, globalStyles.containerBand]}>
        <View style={[globalStyles.horizontalPadGap]}>
          <HomeDetailsHeader
            navLabel="Home"
            title={t('announcements')}
          />
        </View>
      </View>

      <View style={[globalStyles.containerOverlap, styles.contentContainer]}>
        {announcementsList.length > 0 ? (
          announcementsList.map((ann, index) => {
            const isCritical = ann.level === 'C'
            return (
              <View
                key={index}
                style={[
                  styles.announcementCard,
                  isCritical ? styles.cardCritical : styles.cardGeneral
                ]}
              >
                <View style={styles.cardHeader}>
                  <View style={styles.headerLeft}>
                    <Entypo
                      name={isCritical ? 'warning' : 'info-with-circle'}
                      size={16}
                      color={isCritical ? '#D81616' : '#0B495C'}
                      style={styles.headerIcon}
                    />
                    <Text style={styles.badgeTextDate}>
                      {ann.postDate ? moment(ann.postDate).format('MMM DD, YYYY') : ''}
                    </Text>
                  </View>
                </View>

                <Text style={styles.announcementDescription}>{ann.description}</Text>

                <View style={styles.cardFooter}>
                  <Text style={styles.languageText}>{t('Language')}: {ann.language}</Text>
                </View>
              </View>
            )
          })
        ) : (
          <View style={styles.emptyContainer}>
            <Entypo name="megaphone" size={48} color="#CCCCCC" />
            <Text style={styles.emptyText}>{t('No announcements available')}</Text>
          </View>
        )}
      </View>
    </ScrollView>
  )
}

export default AnnouncementsScreen

const styles = StyleSheet.create({
  mainContainer: {
    flex: 1,
    backgroundColor: '#F5F5F5'
  },
  contentContainer: {
    paddingBottom: 20
  },
  iconContainer: {
    backgroundColor: '#9DD086',
    borderRadius: 32,
    width: 32,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 8
  },
  announcementCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderLeftWidth: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2
  },
  cardCritical: {
    borderLeftColor: '#D81616'
  },
  cardGeneral: {
    borderLeftColor: '#0B495C'
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  headerIcon: {
    marginRight: 6
  },
  badgeTextDate: {
    fontSize: 12,
    color: '#7F7F7F',
    fontFamily: fontPalette.primarySemiBold
  },
  announcementDescription: {
    fontSize: 15,
    color: '#1D1D1D',
    lineHeight: 22,
    fontFamily: fontPalette.primarySemiBold
  },
  cardFooter: {
    marginTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#F2F2F2',
    paddingTop: 8,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center'
  },
  languageText: {
    fontSize: 11,
    color: '#7F7F7F',
    fontFamily: fontPalette.primarySemiBold,
    textTransform: 'uppercase'
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
    backgroundColor: 'white',
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2
  },
  emptyText: {
    textAlign: 'center',
    fontSize: 15,
    color: '#7F7F7F',
    fontFamily: fontPalette.primarySemiBold,
    marginTop: 12
  }
})

